import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { installWriteThrough, setSyncUser, cancelPendingSync } from '../lib/cloud/writeThrough';
import { clearSyncedUserDataOnLogout } from '../lib/cloud/localCache';
import { resetReconcile } from '../lib/cloud/reconcile';

interface AuthContextValue {
  /** Current Supabase session, or null when logged out. */
  session: Session | null;
  /** Convenience accessor for the logged-in user (session.user) or null. */
  user: User | null;
  /** True until the initial session has been resolved on boot. */
  loading: boolean;
  /**
   * True when the current session came from a password-reset link
   * (Supabase's PASSWORD_RECOVERY event). Such a session is "recovery-only":
   * guards must keep it on /reset-password and NOT let it into the app until
   * the password is actually updated. Cleared via `clearRecovery()`.
   */
  isRecoverySession: boolean;
  /** Clear the recovery flag once the password has been updated. */
  clearRecovery: () => void;
  /** Sign out the current user. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  loading: true,
  isRecoverySession: false,
  clearRecovery: () => {},
  signOut: async () => {},
});

// Persist the recovery flag for the tab so a reload of /reset-password keeps the
// recovery-only state (the PASSWORD_RECOVERY event only fires once, when the URL
// token is first consumed). sessionStorage is per-tab and cleared on close.
const RECOVERY_KEY = 'mycryptoplanner_recovery_session';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecoverySession, setIsRecoverySession] = useState<boolean>(() => {
    try { return sessionStorage.getItem(RECOVERY_KEY) === '1'; }
    catch { return false; }
  });

  const markRecovery = (on: boolean) => {
    setIsRecoverySession(on);
    try {
      if (on) sessionStorage.setItem(RECOVERY_KEY, '1');
      else sessionStorage.removeItem(RECOVERY_KEY);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let active = true;

    // Install the localStorage -> cloud write-through once. Idempotent, so the
    // StrictMode double-mount is harmless. Purely additive: localStorage writes
    // are unchanged; synced keys are also mirrored to Supabase for the current
    // user in the background (no-op while logged out).
    installWriteThrough();

    // 1. Resolve the persisted session synchronously-ish on boot.
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    // 2. Keep state in sync with sign-in / sign-out / token-refresh events.
    //    A PASSWORD_RECOVERY event means the user arrived via a reset link — flag
    //    the session as recovery-only so guards route them to /reset-password.
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'PASSWORD_RECOVERY') markRecovery(true);
      else if (event === 'SIGNED_OUT') markRecovery(false);
      setSession(newSession);
      setLoading(false);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Keep the cloud write-through pointed at the logged-in user. While null, the
  // write-through is inert and the app behaves exactly as a localStorage-only app.
  useEffect(() => {
    setSyncUser(session?.user?.id ?? null);
  }, [session]);

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    loading,
    isRecoverySession,
    clearRecovery: () => markRecovery(false),
    signOut: async () => {
      // Detach the sync user and cancel any queued cloud pushes BEFORE clearing
      // local data, so the clear can never be mirrored up (an empty array pushed
      // under the still-current user id would wipe their cloud rows).
      setSyncUser(null);
      cancelPendingSync();
      await supabase.auth.signOut();
      // Clear this device's cached user data (display prefs are preserved) and
      // reset the reconcile guard so the next login re-hydrates from the cloud.
      clearSyncedUserDataOnLogout();
      resetReconcile();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
